<script setup>
import { computed, onMounted, ref } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import http from '@/api/http'
import Button from 'primevue/button'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Tag from 'primevue/tag'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Message from 'primevue/message'

const toast = useToast()
const confirm = useConfirm()

const rows = ref([])
const loading = ref(false)
const error = ref('')

/** Диалог показа секрета: создание или перегенерация */
const showSecretDialog = ref(false)
const secretDialogMode = ref('create')
const revealedSecret = ref(null)

const secretDialogHeader = computed(() =>
  secretDialogMode.value === 'regenerate' ? 'Новый секрет токена' : 'Новый API-токен',
)

const secretWarnText = computed(() =>
  secretDialogMode.value === 'regenerate'
    ? 'Предыдущий секрет перестаёт работать. Сохраните новый — он показывается только один раз.'
    : 'Сохраните секрет сейчас — он показывается только один раз.',
)

const newDescription = ref('')
const creating = ref(false)

function openCreateDialog() {
  secretDialogMode.value = 'create'
  revealedSecret.value = null
  showSecretDialog.value = true
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const { data } = await http.get('/tokens')
    rows.value = data?.items ?? []
  } catch (e) {
    error.value = e?.response?.data?.error?.message || 'Не удалось загрузить токены'
  } finally {
    loading.value = false
  }
}

async function createToken() {
  if (!newDescription.value.trim()) return
  creating.value = true
  try {
    const { data } = await http.post('/tokens', { description: newDescription.value.trim() })
    revealedSecret.value = data?.token ?? null
    newDescription.value = ''
    await load()
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: e?.response?.data?.error?.message || 'Не удалось создать токен',
      life: 4000,
    })
  } finally {
    creating.value = false
  }
}

async function patchActive(row, active) {
  try {
    await http.patch(`/tokens/${row.id}`, { active })
    toast.add({
      severity: 'success',
      summary: 'Готово',
      detail: active ? 'Токен включён' : 'Токен отключён',
      life: 3000,
    })
    await load()
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: e?.response?.data?.error?.message || 'Не удалось изменить статус',
      life: 4000,
    })
  }
}

function deactivate(row) {
  confirm.require({
    message: `Отключить токен «${row.description ?? row.key}»? Запросы с этим ключом перестанут проходить.`,
    header: 'Подтверждение',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Отмена',
    acceptLabel: 'Отключить',
    acceptProps: { severity: 'danger' },
    accept: () => patchActive(row, false),
  })
}

function activateToken(row) {
  patchActive(row, true)
}

function requestRegenerate(row) {
  confirm.require({
    message: `Перегенерировать секрет для «${row.description ?? row.key}»? Текущий секрет сразу перестанет действовать.`,
    header: 'Подтверждение',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Отмена',
    acceptLabel: 'Перегенерировать',
    acceptProps: { severity: 'warn' },
    accept: async () => {
      try {
        const { data } = await http.post(`/tokens/${row.id}/regenerate`)
        secretDialogMode.value = 'regenerate'
        revealedSecret.value = data?.token ?? null
        showSecretDialog.value = true
        await load()
      } catch (e) {
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: e?.response?.data?.error?.message || 'Не удалось перегенерировать',
          life: 4000,
        })
      }
    },
  })
}

function copySecret() {
  if (!revealedSecret.value) return
  const full = `${revealedSecret.value.key}:${revealedSecret.value.secret}`
  navigator.clipboard?.writeText(full)
  toast.add({ severity: 'info', summary: 'Скопировано', life: 2000 })
}

function copyHeader() {
  if (!revealedSecret.value?.authorization_header) return
  navigator.clipboard?.writeText(revealedSecret.value.authorization_header)
  toast.add({ severity: 'info', summary: 'Заголовок скопирован', life: 2000 })
}

function fmtDate(s) {
  if (!s) return '—'
  try {
    return new Date(s).toLocaleString('ru-RU')
  } catch (_) {
    return s
  }
}

function onSecretDialogHide() {
  newDescription.value = ''
  revealedSecret.value = null
  secretDialogMode.value = 'create'
}

onMounted(load)
</script>

<template>
  <div class="page">
    <div class="toolbar">
      <Button icon="pi pi-plus" label="Создать токен" @click="openCreateDialog" />
      <div class="toolbar__spacer" />
      <Button
        icon="pi pi-refresh"
        label="Обновить"
        severity="secondary"
        outlined
        @click="load"
        :loading="loading"
      />
    </div>

    <p v-if="error" class="error">{{ error }}</p>

    <DataTable :value="rows" :loading="loading" data-key="id" striped-rows>
      <Column field="description" header="Описание">
        <template #body="{ data }">{{ data.description || '—' }}</template>
      </Column>
      <Column field="key" header="Key">
        <template #body="{ data }"><code>{{ data.key }}</code></template>
      </Column>
      <Column header="Статус" style="width: 8rem">
        <template #body="{ data }">
          <Tag v-if="data.active" value="активен" severity="success" />
          <Tag v-else value="отключён" severity="secondary" />
        </template>
      </Column>
      <Column header="Создан" style="width: 12rem">
        <template #body="{ data }">{{ fmtDate(data.date_create) }}</template>
      </Column>
      <Column header="Истекает" style="width: 12rem">
        <template #body="{ data }">{{ fmtDate(data.date_expire) }}</template>
      </Column>
      <Column header="Использован" style="width: 12rem">
        <template #body="{ data }">{{ fmtDate(data.last_used) }}</template>
      </Column>
      <Column header="" style="width: 11rem">
        <template #body="{ data }">
          <div class="row-actions">
            <Button
              text
              rounded
              severity="secondary"
              icon="pi pi-refresh"
              aria-label="Перегенерировать секрет"
              @click="requestRegenerate(data)"
            />
            <Button
              v-if="data.active"
              text
              rounded
              severity="danger"
              icon="pi pi-ban"
              aria-label="Отключить"
              @click="deactivate(data)"
            />
            <Button
              v-else
              text
              rounded
              severity="success"
              icon="pi pi-check"
              aria-label="Включить"
              @click="activateToken(data)"
            />
          </div>
        </template>
      </Column>
      <template #empty>
        <p class="muted">Токенов пока нет</p>
      </template>
    </DataTable>

    <Dialog
      v-model:visible="showSecretDialog"
      modal
      :header="secretDialogHeader"
      :style="{ width: '32rem' }"
      :closable="!creating"
      @hide="onSecretDialogHide"
    >
      <div v-if="!revealedSecret" class="dialog-body">
        <p class="muted">Описание — для удобства, отображается в списке.</p>
        <InputText
          v-model="newDescription"
          placeholder="Например: интеграция 1С"
          fluid
          autofocus
          @keyup.enter="createToken"
        />
      </div>
      <div v-else class="dialog-body">
        <Message severity="warn" :closable="false">
          {{ secretWarnText }}
        </Message>
        <div class="secret">
          <div><strong>Key:</strong> <code>{{ revealedSecret.key }}</code></div>
          <div><strong>Secret:</strong> <code>{{ revealedSecret.secret }}</code></div>
          <div>
            <strong>Authorization:</strong>
            <code>{{ revealedSecret.authorization_header }}</code>
          </div>
        </div>
      </div>

      <template #footer>
        <template v-if="!revealedSecret">
          <Button label="Отмена" text @click="showSecretDialog = false" :disabled="creating" />
          <Button label="Создать" icon="pi pi-check" :loading="creating" @click="createToken" />
        </template>
        <template v-else>
          <Button label="key:secret" icon="pi pi-copy" severity="secondary" @click="copySecret" />
          <Button label="Заголовок" icon="pi pi-copy" @click="copyHeader" />
          <Button label="Готово" text @click="showSecretDialog = false" />
        </template>
      </template>
    </Dialog>
  </div>
</template>

<style scoped>
.toolbar {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 1rem;
}
.toolbar__spacer {
  flex: 1;
}
.error {
  color: #b91c1c;
  margin: 0 0 0.75rem;
}
.secret {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  padding: 0.75rem 1rem;
  margin-top: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.secret code {
  font-size: 0.85rem;
}
.dialog-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.row-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  justify-content: flex-end;
}
</style>
